/**
 * PATH: packages/email-hub/src/feature/reader-header/notices/buildEmailNotices.ts
 * FILE_ID: EMAILHUB-READERHEADER-NOTICES-BUILD-001
 * PURPOSE: Derive registry-stamped notices from email payload state (pure selector).
 * ROLE IN ARCHITECTURE: Selector between email data and the notice summary line.
 * MAIN EXPORTS: buildEmailNotices.
 * NON-RESPONSIBILITIES: This selector never fetches. Provider writeback state
 *   arrives on `email.provider_writeback`; ping detection is the caller's
 *   `usePingHandoff` result passed in via `options.pingHandoff` (change #269
 *   Notice Center unification — mirrors the pre-existing `detailError`
 *   precedent for "caller already has this, pass it in").
 * NOTES FOR FUTURE AI: Only derive notices from fields already on the Email
 *   payload (plus the two passed-in options) — the plan forbids new fetches
 *   before first body paint. Severity/collapse/action-set ordering is owned
 *   by noticeRegistry.ts; never hardcode it here.
 */
import type { Email } from '../../hooks/useEmails'
import type { MyHandoff } from '../../components/usePingHandoff'
import { PROVIDER_WRITEBACK_LABELS } from '../../lib/writebackContract'
import type { ReaderNotice } from '../model/types'
import { createReaderNotice, sortReaderNotices } from './noticeRegistry'

const CALENDAR_LABELS: Record<string, string> = {
  meetingRequest: 'Meeting invitation awaiting a response',
  meetingCancelled: 'Meeting cancelled by the organiser',
  meetingAccepted: 'Meeting accepted',
  meetingTentativelyAccepted: 'Meeting tentatively accepted',
  meetingDeclined: 'Meeting declined',
  meetingForwardNotification: 'Meeting invitation forwarded',
}

/**
 * True when the email carries attachments the list said exist but the
 * detail payload hasn't hydrated yet. Shared predicate (already used by the
 * two legacy hydrating-placeholder surfaces —
 * ReaderWorkflowSections/PreviewReadingSurface) lifted here so the Notice
 * Center agrees with them on what "still loading" means.
 */
function attachmentsStillHydrating(email: Email): boolean {
  return email.has_attachments && (email.attachments?.length ?? 0) === 0
}

export function buildEmailNotices(
  email: Email,
  options: {
    detailError?: string | null
    /** Result of the caller's `usePingHandoff(email.id)` call — this
     *  selector never fetches it itself. */
    pingHandoff?: MyHandoff | null
  } = {},
): ReaderNotice[] {
  const notices: ReaderNotice[] = []

  if (options.detailError) {
    notices.push(
      createReaderNotice({
        id: `error:${email.id}`,
        kind: 'error',
        label: options.detailError,
        actionLabel: 'Retry',
      }),
    )
  }

  const writeback = email.provider_writeback
  if (writeback) {
    if (writeback.result === 'failed') {
      notices.push(
        createReaderNotice({
          id: `provider:${email.id}`,
          kind: 'provider',
          label: PROVIDER_WRITEBACK_LABELS.failed,
          actionLabel: 'Retry',
          lifecycle: 'active',
        }),
      )
    } else {
      // requested/running/completed/restored/skipped — informational only,
      // still expandable to the real ProviderWritebackControls band, never
      // an actionLabel (Retry/Not Done are gated on the real state inside
      // that component, not on this summary line).
      notices.push(
        createReaderNotice({
          id: `provider:${email.id}`,
          kind: 'info',
          label: PROVIDER_WRITEBACK_LABELS[writeback.result],
        }),
      )
    }
  }

  if (options.pingHandoff) {
    const who = (options.pingHandoff.assigned_by_name || 'A teammate').trim()
    notices.push(
      createReaderNotice({
        id: `ping:${options.pingHandoff.id}`,
        kind: 'ping',
        label: `${who} pinged you`,
      }),
    )
  }

  const meetingType = email.calendar_event?.meeting_message_type
  if (
    (email.has_calendar_event || email.calendar_event) &&
    meetingType !== 'none'
  ) {
    const pending = meetingType === 'meetingRequest'
    notices.push(
      createReaderNotice({
        id: `calendar:${email.id}`,
        kind: 'calendar',
        label:
          (meetingType && CALENDAR_LABELS[meetingType]) ||
          'Calendar event attached',
        lifecycle: pending ? 'pending' : 'acknowledged',
        actionLabel: pending ? 'View event' : undefined,
      }),
    )
  }

  if (attachmentsStillHydrating(email)) {
    const count = email.attachment_count || email.attachments?.length || 0
    notices.push(
      createReaderNotice({
        id: `attachments:${email.id}`,
        kind: 'attachments',
        // Passive only (registry NOTES): no real retry/failure path exists
        // today, so this never gets an actionLabel — a button that does
        // nothing is worse than no button.
        label: `Downloading ${count} attachment${count === 1 ? '' : 's'}`,
      }),
    )
  }

  return sortReaderNotices(notices)
}
